import { t } from 'ttag'
import { getRecruiterTraits } from 'components/pages/Help/helpScoreCardText'
import { VALUE_LEVEL_COLUMN } from './components/TalentAreasTable/model'
import {
  formatDominantPattern,
  formatKeyContrast,
  formatWatchFor,
} from './keyObservationsCopy'

export const ZONE = {
  CORE: 'core',
  POTENTIAL: 'potential',
  REQUIRES_ENERGY: 'requiresEnergy',
}

export const ZONE_ORDER = [ZONE.CORE, ZONE.POTENTIAL, ZONE.REQUIRES_ENERGY]

const MATCH_CORE_MIN = 60
const MATCH_REQUIRES_ENERGY_MAX = 40

const PERSONALITY_HIGH = 70
const PERSONALITY_LOW = 40
const INTEREST_HIGH = 70
const INTEREST_LOW = 50

const isHighPersonality = score => score >= PERSONALITY_HIGH
const isModeratePersonality = score =>
  score >= PERSONALITY_LOW && score < PERSONALITY_HIGH
const isHighInterest = score => score >= INTEREST_HIGH
const isModerateInterest = score =>
  score > INTEREST_LOW && score < INTEREST_HIGH

export const getPersonalityScoreLevel = score => {
  if (isHighPersonality(score)) {
    return 'high'
  }
  if (isModeratePersonality(score)) {
    return 'moderate'
  }
  return 'low'
}

export const getInterestScoreLevel = score => {
  if (isHighInterest(score)) {
    return 'high'
  }
  if (isModerateInterest(score)) {
    return 'moderate'
  }
  return 'low'
}

export const BIG_FIVE_FACTORS = [
  { key: 'Agreeableness', label: () => t`Agreeableness` },
  { key: 'Conscientiousness', label: () => t`Conscientiousness` },
  { key: 'Extraversion', label: () => t`Extraversion` },
  { key: 'Openness to Experience', label: () => t`Openness` },
  { key: 'Neuroticism', label: () => t`Neuroticism` },
]

export const getTalentZone = ({ matchScore = 0 }) => {
  if (matchScore >= MATCH_CORE_MIN) {
    return ZONE.CORE
  }

  if (matchScore < MATCH_REQUIRES_ENERGY_MAX) {
    return ZONE.REQUIRES_ENERGY
  }

  return ZONE.POTENTIAL
}

export const withTalentZones = talents =>
  talents.map(talent => ({
    ...talent,
    zone: getTalentZone(talent),
  }))

const getTalentSortValue = (talent, field) => {
  if (field === 'zone') {
    return ZONE_ORDER.indexOf(talent.zone)
  }

  if (field === VALUE_LEVEL_COLUMN) {
    return talent.value ?? talent.matchScore ?? 0
  }

  return talent[field]
}

export const orderTalents = (talents, ordering) => {
  const sortField = ordering?.ascending ?? ordering?.descending

  if (!sortField) {
    return talents
  }

  const isAscending = Boolean(ordering.ascending)

  return [...talents].sort((a, b) => {
    const aValue = getTalentSortValue(a, sortField)
    const bValue = getTalentSortValue(b, sortField)

    if (aValue > bValue) {
      return isAscending ? 1 : -1
    }
    if (aValue < bValue) {
      return isAscending ? -1 : 1
    }

    const aTie = getTalentSortValue(a, VALUE_LEVEL_COLUMN)
    const bTie = getTalentSortValue(b, VALUE_LEVEL_COLUMN)

    if (isAscending) {
      if (aTie > bTie) {
        return 1
      }
      if (aTie < bTie) {
        return -1
      }
    } else {
      if (aTie < bTie) {
        return 1
      }
      if (aTie > bTie) {
        return -1
      }
    }

    return 0
  })
}

export const groupTalentsByZone = talents => {
  const grouped: Record<
    string,
    ({ matchScore: number } & Record<string, any>)[]
  > = {
    [ZONE.CORE]: [],
    [ZONE.POTENTIAL]: [],
    [ZONE.REQUIRES_ENERGY]: [],
  }

  talents.forEach(talent => {
    grouped[talent.zone].push(talent)
  })

  ZONE_ORDER.forEach(zone => {
    grouped[zone].sort((a, b) => b.matchScore - a.matchScore)
  })

  return grouped
}

export const getBigFiveScores = scores =>
  BIG_FIVE_FACTORS.map(({ key, label }) => ({
    key,
    label: label(),
    score: Math.round((scores?.[key] ?? 0) * 100),
  }))

export const getInitials = name => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase()
  }
  return (name || '??').slice(0, 2).toUpperCase()
}

const getTopInterestArea = talents => {
  if (!talents?.length) {
    return null
  }

  return [...talents].sort((a, b) => b.interestScore - a.interestScore)[0]
}

const getLowestInterestArea = talents => {
  if (!talents?.length) {
    return null
  }

  return [...talents].sort((a, b) => a.interestScore - b.interestScore)[0]
}

const getKeyContrastTalent = talents => {
  if (!talents?.length) {
    return null
  }

  return talents.reduce((best, talent) => {
    const gap = Math.abs(talent.personalityScore - talent.interestScore)
    const personalityLevel = getPersonalityScoreLevel(talent.personalityScore)
    const interestLevel = getInterestScoreLevel(talent.interestScore)
    const hasContrast = personalityLevel !== interestLevel

    if (!best) {
      return { ...talent, gap, hasContrast }
    }

    if (hasContrast && !best.hasContrast) {
      return { ...talent, gap, hasContrast }
    }

    if (hasContrast === best.hasContrast && gap > best.gap) {
      return { ...talent, gap, hasContrast }
    }

    return best
  }, null)
}

export const getKeyObservations = ({ scores, talents, language = 'en' }) => {
  const traitDefinitions = getRecruiterTraits(language)
  const bigFive = BIG_FIVE_FACTORS.map(({ key, label }) => ({
    key,
    label: traitDefinitions[key]?.label || label(),
    score: Math.round((scores?.[key] ?? 0) * 100),
  }))
  const dominantFactor = [...bigFive].sort((a, b) => b.score - a.score)[0]
  const lowestFactor = [...bigFive].sort((a, b) => a.score - b.score)[0]
  const topInterestArea = getTopInterestArea(talents)
  const lowestInterestArea = getLowestInterestArea(talents)
  const contrast = getKeyContrastTalent(talents)

  return {
    dominantPattern: formatDominantPattern({
      factor: dominantFactor?.label,
      score: dominantFactor?.score,
      area: topInterestArea?.area,
      interestScore: topInterestArea?.interestScore,
      language,
    }),
    watchFor: formatWatchFor({
      factor: lowestFactor?.label,
      score: lowestFactor?.score,
      area: lowestInterestArea?.area,
      interestScore: lowestInterestArea?.interestScore,
      language,
    }),
    keyContrast: formatKeyContrast({
      area: contrast?.area,
      personalityScore: contrast?.personalityScore,
      interestScore: contrast?.interestScore,
      personalityLevel: contrast
        ? getPersonalityScoreLevel(contrast.personalityScore)
        : null,
      interestLevel: contrast
        ? getInterestScoreLevel(contrast.interestScore)
        : null,
      language,
    }),
  }
}

export const getZoneLabel = zone => {
  switch (zone) {
    case ZONE.CORE:
      return t`Core talent`
    case ZONE.POTENTIAL:
      return t`Potential`
    case ZONE.REQUIRES_ENERGY:
      return t`Requires energy`
    default:
      return zone
  }
}
